/**
 * LodDebugMaterials - material variants for the F7 "LOD" debug view.
 *
 * A terrain batch draws every LOD in one call, so the LOD colour comes from the vertex `lodLevel`
 * attribute inside the shader (LOD_DEBUG_COLORS, terrainShaders.ts); the per-tag variants here only
 * switch the debug MODE on, and give skirt strips their family colour. Nothing costs anything while
 * the view is off.
 *
 * NOT every layer is repainted (see paintsLodColor): flat-colouring foliage and water destroys the
 * two things you still need while reading a LOD map — where the tree canopy is, and where the water
 * line is. Only the SOLID layer takes the LOD colour. Foliage and water keep their own surfaces, and
 * the one exception is their SKIRT strips, which go red like every other skirt.
 */

import * as THREE from 'three';
import { TerrainMaterial, TERRAIN_DEBUG_MODES, getTerrainMaterial } from './TerrainMaterial.js';
import { WaterMaterial } from './WaterMaterial.js';
import { getScene } from '../scene/scene.js';
import { setSkyDomeVisible } from '../scene/SkyDome.js';
import { setLodLevelAttr } from '../voxel/lodTexScale.js';
import { LOD_DEBUG_COLORS, SKIRT_KIND_COLORS, SKIRT_KIND_NAMES } from './terrainShaders.js';

/** What a terrain mesh is coloured by: its LOD level, or the skirt/apron ribbon. */
export type LodTag = number | 'skirt';

/**
 * Skirt + cross-LOD apron strips. Red is reserved for these — no LOD uses it — but it is only the
 * FAMILY colour now, shown on the panel label: the shader overrides it per vertex with one distinct
 * HUE per mechanism (SKIRT_KIND_COLORS in terrainShaders, fan / quad / plane / end / partial). Every
 * seam vertex carries a kind, so this value itself never reaches the screen.
 */
export const SKIRT_DEBUG_COLOR = 0xff0000;

export { LOD_DEBUG_COLORS };

export function lodDebugColor(tag: LodTag): number {
  if (tag === 'skirt') return SKIRT_DEBUG_COLOR;
  const lod = Math.max(0, tag | 0);
  return LOD_DEBUG_COLORS[lod % LOD_DEBUG_COLORS.length];
}

/** CSS hex for the panel legend and the F4 label text. */
export function lodDebugCss(tag: LodTag): string {
  return `#${lodDebugColor(tag).toString(16).padStart(6, '0')}`;
}

/** CSS hex for one seam kind's swatch in the F7 legend — the same table the shader is built from. */
export function skirtKindCss(kind: number): string {
  return `#${(SKIRT_KIND_COLORS[kind] ?? 0).toString(16).padStart(6, '0')}`;
}

export { SKIRT_KIND_NAMES };

export function lodDebugLabel(tag: LodTag): string {
  return tag === 'skirt' ? 'skirt' : `L${tag}`;
}

// ============== Which layers the view repaints ==============

/** Which terrain layer a shared material belongs to (see LAYER_CONFIGS). */
export type LayerKind = 'solid' | 'transparent' | 'liquid';

/**
 * Does this (layer, tag) take the LOD colour, or keep its own material?
 *
 * Solid carries the whole LOD map. Foliage keeps its textures — an opaque leaf quad is a box, and a
 * canopy of boxes hides the terrain underneath. Water keeps its surface for the same reason. Both
 * give up their skirts: a missing bridge is exactly the defect this view is opened to find.
 */
/** Set false to get the canopy and water line back. */
const PAINT_EVERY_LAYER = true;

export function paintsLodColor(kind: LayerKind, tag: LodTag): boolean {
  // EVERY LAYER while a gap is being chased. Foliage and water used to keep their own materials so the canopy and the
  // water line stayed readable — but that also meant two whole layers could render in colours outside
  // the palette, and "the band is grey" could not be told from "the band is a hole". With everything
  // painted, anything NOT a palette colour is definitively not terrain.
  if (PAINT_EVERY_LAYER || kind === 'solid') return true;
  // Both non-solid layers keep their surface and give up their SKIRTS: a missing bridge is the
  // defect the view is opened to find, and a foliage strip that is never red cannot be looked for.
  return tag === 'skirt';
}

// ============== Variant cache ==============

let enabled = false;

/** Keyed by `${shared material uuid}:${tag}` — one variant per (layer material, LOD). */
const variants = new Map<string, TerrainMaterial | WaterMaterial>();

/** The shared layer materials are all MeshStandardMaterial subclasses (see LAYER_CONFIGS). */
type TerrainLayerMaterial = THREE.MeshStandardMaterial;

/** Read the layer back off the material itself, so this can never drift from LAYER_CONFIGS. */
function layerKindOf(base: TerrainLayerMaterial): LayerKind {
  if (base instanceof WaterMaterial) return 'liquid';
  // Exact round-trip of the constructor arg: TerrainMaterial(true) is the alpha-tested one.
  return base.alphaTest > 0 ? 'transparent' : 'solid';
}

function variantFor(base: TerrainLayerMaterial, tag: LodTag): THREE.Material {
  const key = `${base.uuid}:${tag}`;
  const cached = variants.get(key);
  if (cached) return cached;

  let variant: TerrainMaterial | WaterMaterial;
  if (base instanceof WaterMaterial) {
    variant = new WaterMaterial();
  } else {
    variant = new TerrainMaterial(base.alphaTest > 0);
  }
  const textures = getTerrainMaterial().getTextures();
  if (textures) variant.setTextures(textures);
  variant.setLodColor(lodDebugColor(tag));
  variant.setDebugMode(TERRAIN_DEBUG_MODES.LOD);
  variant.wireframe = base.wireframe;
  // DOUBLE-SIDED ON PURPOSE: the shader paints back faces magenta, which turns every hole into a
  // colour instead of a judgement about what is behind it.
  variant.side = THREE.DoubleSide;

  variants.set(key, variant);
  return variant;
}

// ============== Mesh tagging ==============

interface LodTaggedMesh extends THREE.Mesh {
  userData: { terrainBaseMaterial?: TerrainLayerMaterial; lodTag?: LodTag } & Record<string, unknown>;
}

/**
 * Mark a terrain mesh as belonging to `tag` and give it the material the current mode wants.
 * Safe to call again when a mesh is reused at another LOD (standalone slots do exactly that).
 */
export function applyLodTag(mesh: THREE.Mesh, tag: LodTag): void {
  const m = mesh as LodTaggedMesh;
  // A retag must not record the variant the mesh is currently wearing as its base material.
  const base = m.userData.terrainBaseMaterial ?? (mesh.material as TerrainLayerMaterial);
  m.userData.terrainBaseMaterial = base;
  m.userData.lodTag = tag;
  // A batch is never stamped: its geometry mixes levels, each copied in with its own lodLevel. Skirts
  // neither — a strip's level is its OWNER's, which this function is not told.
  if (tag !== 'skirt' && !(mesh instanceof THREE.BatchedMesh)) setLodLevelAttr(mesh.geometry, tag);
  mesh.material = enabled && paintsLodColor(layerKindOf(base), tag) ? variantFor(base, tag) : base;
}

export function isLodDebugEnabled(): boolean {
  return enabled;
}

/**
 * Enter/leave the LOD view. Swaps materials on every tagged mesh already in the scene; meshes built
 * afterwards pick the right one up through applyLodTag.
 */
/** The scene's own background, parked while the view borrows it. */
let savedBackground: THREE.Scene['background'] | undefined;

/**
 * A HOLE IS A COLOUR, NOT A JUDGEMENT.
 *
 * Back-face painting is not enough on its own: terrain has no meshed underside, so looking through a
 * gap usually shows no geometry at all and the hole reads as whatever the sky happens to be — which is
 * exactly the "is that a hole or a dark surface?" call that is impossible to make from a screenshot.
 * Parking one NEUTRAL colour behind everything settles it: the palette carries every meaning, so
 * magenta is terrain's inside seen through a gap, and this grey is nothing at all.
 *
 * Deliberately dark and unsaturated, not loud. A loud backdrop competes with the palette it exists to
 * frame — the orange it used to be was a shade off lod 3's, so "coarse chunk" and "no chunk" looked
 * alike — and it fed bloom and the god rays a full-screen bright surface.
 */
const LOD_DEBUG_BACKDROP = 0x202020;

export function setLodDebugEnabled(on: boolean): void {
  if (on === enabled) return;
  enabled = on;
  if (on) refreshLodDebugTextures(); // the atlas may have upgraded since these were built

  const scene = getScene();
  if (!scene) return;
  if (on) {
    savedBackground = scene.background;
    scene.background = new THREE.Color(LOD_DEBUG_BACKDROP);
  } else if (savedBackground !== undefined) {
    scene.background = savedBackground;
    savedBackground = undefined;
  }
  // ...and the sky is a MESH, so the background is invisible behind it. Without this the backdrop
  // does nothing and every hole keeps reading as whatever the dome happens to be there.
  setSkyDomeVisible(!on);
  scene.traverse((obj) => {
    const m = obj as LodTaggedMesh;
    const base = m.userData?.terrainBaseMaterial;
    if (!base) return;
    const tag = m.userData.lodTag ?? 0;
    m.material = on && paintsLodColor(layerKindOf(base), tag) ? variantFor(base, tag) : base;
  });
}

/** Move the variants onto the atlas the shared materials are on (resolution upgrade). */
export function refreshLodDebugTextures(): void {
  const textures = getTerrainMaterial().getTextures();
  if (!textures) return;
  for (const v of variants.values()) v.setTextures(textures);
}

/** Mirror the F5 wireframe toggle — setVoxelWireframe only reaches the shared materials. */
export function setLodDebugWireframe(on: boolean): void {
  for (const v of variants.values()) v.wireframe = on;
}
